import type { SupportedLocale } from '@fleetcore/shared'
import type { LiveVehicleView } from '@fleetcore/shared/telemetry'
import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import { currentLocale } from '../../i18n'
import { fetchCapabilities, fetchLatestTelemetry, fetchVehicle, fetchVehicleLive } from './api'
import type {
  CapabilityProfile,
  LatestTelemetryView,
  LiveVehicleEnvelope,
  VehicleView,
} from './api-types'

/**
 * Four independent queries rather than one aggregate.
 *
 * They fail independently and they must degrade independently: a vehicle whose
 * Redis projection is missing still has an identity, a capability profile and a
 * telemetry history, and a page that awaited all four before painting would show
 * an empty screen for the case the empty states were written to explain.
 */

export const vehicleQueryKeys = {
  detail: (id: string) => ['vehicle', id, 'detail'] as const,
  live: (id: string) => ['vehicle', id, 'live'] as const,
  latest: (id: string, locale: SupportedLocale) => ['vehicle', id, 'telemetry', locale] as const,
  capabilities: (id: string, locale: SupportedLocale) =>
    ['vehicle', id, 'capabilities', locale] as const,
}

/** How often the live query re-asks when no socket is feeding the cache. */
export const LIVE_POLL_INTERVAL_MS = 10_000

export function useVehicle(id: string): UseQueryResult<VehicleView> {
  return useQuery({
    queryKey: vehicleQueryKeys.detail(id),
    queryFn: () => fetchVehicle(id),
    // Identity changes when someone edits the record, not on its own.
    staleTime: 60_000,
  })
}

/**
 * Live state for one vehicle.
 *
 * The poll is the FALLBACK, not the transport. `live:vehicle_update` on the
 * vehicle room carries the new state, and the socket layer writes it straight into
 * this cache entry with `applyLiveVehicleUpdate` below — an event must never
 * trigger a refetch (invariant 3). The interval only covers a dropped socket.
 */
export function useVehicleLive(id: string): UseQueryResult<LiveVehicleEnvelope> {
  return useQuery({
    queryKey: vehicleQueryKeys.live(id),
    queryFn: () => fetchVehicleLive(id),
    refetchInterval: LIVE_POLL_INTERVAL_MS,
    staleTime: 0,
  })
}

export function useLatestTelemetry(id: string): UseQueryResult<LatestTelemetryView> {
  const locale = currentLocale()
  return useQuery({
    queryKey: vehicleQueryKeys.latest(id, locale),
    queryFn: () => fetchLatestTelemetry(id, locale),
    refetchInterval: LIVE_POLL_INTERVAL_MS * 3,
    staleTime: 15_000,
  })
}

export function useCapabilities(id: string): UseQueryResult<CapabilityProfile> {
  const locale = currentLocale()
  return useQuery({
    queryKey: vehicleQueryKeys.capabilities(id, locale),
    queryFn: () => fetchCapabilities(id, locale),
    // A capability profile is a 30-day window; it does not move between renders.
    staleTime: 5 * 60_000,
  })
}

/**
 * Merge one `live:vehicle_update` into the cache, in place.
 *
 * The WS payload is a `LiveVehicleView` — state and connectivity, no identity —
 * while the cached entry is the REST envelope, which carries registration, class
 * and device. Overwriting the entry with the payload would blank the header; the
 * merge is what lets the socket update a marker without a refetch.
 *
 * Exported for whoever owns the socket client: this is the seam, and it is here so
 * that the page and the socket cannot disagree about the shape.
 */
export function applyLiveVehicleUpdate(
  client: QueryClient,
  vehicleId: string,
  view: LiveVehicleView,
): void {
  client.setQueryData<LiveVehicleEnvelope>(vehicleQueryKeys.live(vehicleId), (previous) => {
    if (previous === undefined) return previous
    return {
      ...previous,
      state: view,
      connectivity: view.connectivity,
      lastSeenAt: view.receivedAt,
      ageSeconds: view.ageSeconds,
      absenceReason: null,
    }
  })
}

export function useApplyLiveVehicleUpdate(): (id: string, view: LiveVehicleView) => void {
  const client = useQueryClient()
  return (id, view) => applyLiveVehicleUpdate(client, id, view)
}
